import * as React from 'react';
import { MagicField } from '../../Enums/MagicField.enum';
import FormControl from './FormControl';
import RadioList from '../Base/RadioList/RadioList';
import Label from '../Base/Label/Label';
import Checkbox from '../Base/Checkbox/Checkbox';
import Select from '../Base/Select/Select';
import DiagnosisLatam from '../Base/Diagnosis/DiagnosisLatam';
import MagicFieldText from './MagicField/MagicFieldText';
import MagicFieldTextField from './MagicField/MagicFieldTextField';
import FormMultiplier from './FormMultiplier';
import Collapse from '../HealthPlan/Collapse/Collapse';
import FormCondition from './FormCondition';
import FormContainer from './FormContainer';
import BMIFormControl from '../Base/BMI/BMIFormControl';
import HStack from '../Base/Containers/HStack';
import MagicFieldImage from './MagicField/MagicFieldImage';
import useTranslation from '../../Hooks/useTranslation';
import GlycaemiaFormControl from '../Base/Glycaemia/GlycaemiaFormControl';
import QuestionnaireSelect from '../../Modules/Questionnaire/Components/QuestionnaireSelect/QuestionnaireSelect';
import PatientSelect from '../../Modules/Patient/Components/PatientSelect/PatientSelect';
import DoctorSelect from '../../Modules/Doctor/Components/DoctorSelect/DoctorSelect';
import FormMultiplierRemoveButton from './FormMultiplierRemoveButton';
import RemoveButton from '../Button/RemoveButton';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import Tooltip from '../Popup/Tooltip';
import { NCZI_OID } from '../../Modules/Nczi/Enums/ncziOid.enum';
import NCZIReferenceSelectField from '../../Modules/Nczi/Components/ReferenceSelect/NCZIReferenceSelectField';
import InputWrapper from './components/InputWrapper/InputWrapper';
import WorkplaceOrganizationUnitSelect from '../../Modules/Workplace/Components/WorkplaceOrganizationUnitSelect/WokrplaceOrganizationUnitSelect';
import { useAppContext } from '../../Services/AppContext';
import FormattedPriceInputField from '../../Modules/Base/Components/Inputs/FormattedPriceInput/FormattedPriceInputField';
import PhoneNumberField from '../PhoneNumber/PhoneNumberField';
import RichTextField from '../../Modules/Base/Components/Inputs/RichTextField/RichTextField';
import NCZIReferenceSearchSelectField from '../../Modules/Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import DiagnosisSelectField from '../Base/Diagnosis/DiagnosisSelectField';
import HealthCareReimbursementVzpSelectField from '../../Modules/Base/Components/Inputs/HealthCareReimbursementVzpSelect/HealthCareReimbursementVzpSelectField';
import HealthCareRegulationCaseTypeSelectField from '../../Modules/Base/Components/Inputs/HealthCareRegulationCaseTypeSelect/HealthCareRegulationCaseTypeSelectField';
import QuestionnaireScheduleFrequencySelectField from '../../Modules/Base/Components/Inputs/QuestionnaireScheduleFrequencySelect/QuestionnaireScheduleFrequencySelectField';
import TextAreaField from './components/TextAreaField';
import { useMagicItemRules } from '../../Modules/MagicForm/Hooks/useMagicItemRules';
import FieldOfCareSelectField from '../../Modules/Base/Components/Inputs/FieldOfCareSelect/FieldOfCareSelectField';
import CitizenshipSelectField from '../../Modules/Base/Components/Fields/Citizenship/CitizenshipSelectField';
import HealthInsuranceCompanySelectField from '../../Modules/Base/Components/Inputs/HealthInsuranceCompanySelect/HealthInsuranceCompanySelectField';
import GenderSelectField from '../../Modules/Base/Components/Inputs/GenderSelect/GenderSelectField';
import WorkplaceSelectField from '../../Modules/Base/Components/Inputs/WorkplaceSelect/WorkplaceSelectField';
import { QuestionnaireModule } from '../../Modules/Questionnaire/Enums/QuestionnaireModule.enum';
import PatientNoConsentProvided from '../../Modules/Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import PrescriptionMedicamentsSelect from '../../Modules/Prescription/Components/PrescriptionMedicamentsSelect';
import MagicFormTranslations from '../../Modules/MagicForm/Components/MagicFormTranslations';
import VATRateSelectField from '../../Modules/Base/Components/Inputs/VATRateSelect/VATRateSelectField';
import FormOfCareSelectField from '../../Modules/Base/Components/Inputs/FormOfCareSelect/FormOfCareSelectField';
import NumberField from './components/NumberField';
import RoleMultiSelectWithCheckListField from '../../Modules/Base/Components/Inputs/RoleMultiSelectWithCheckList/RoleMultiSelectWithCheckListField';
import { WorkplaceAclRole } from '../../Modules/Workplace/Enums/WorkplaceAclRole.enum';
import { set } from '../../Services/LocalStorage';
import TermItemSelectField from '../../Modules/Term/Components/TermItemSelectField';
import P1PaymentCoverageTypeField from '../../Modules/Base/Components/Fields/P1PaymentCoverageType/P1PaymentCoverageTypeField';
import P1DosageIntervalTypeField from '../../Modules/Base/Components/Fields/P1DosageIntervalType/P1DosageIntervalTypeField';
import P1MedicamentFiled from '../../Modules/Base/Components/Fields/P1Medicament/P1MedicamentField';

interface MagicFormItemProps {
	name?: string;
	itemScheme: ItemScheme;
	disabled?: boolean;
	showCharCounter?: boolean;
}

type ItemSchemeBase = {
	id?: string;
	type: MagicField;
	key?: string;
	subType?: string;
	label?: string;
	value?: string;
	title?: string;
	name?: string;
	options?: Array<object>;
	onChange?: () => void;
	defaultValue?: any;
	inLine?: boolean;
	settings?: ItemSchemeSettings | any;
};

type FieldsWithoutType = MagicField.RADIOLIST | MagicField.SELECT;
type FieldsWithType = Exclude<MagicField, FieldsWithoutType>;

export type ItemScheme =
	| ({
			type: FieldsWithType;
			children?: ItemScheme[];
	  } & ItemSchemeBase)
	| ({
			type: FieldsWithoutType;
			children: Omit<ItemScheme, 'type'>[];
	  } & ItemSchemeBase);

interface ItemSchemeSettings {
	labelStyle?: object;
	toggle?: string;
	trigger?: React.ReactNode;
	suffix?: string;
	required?: boolean;
	showLine?: boolean;
	rules?: any[];
	minuteStep?: number;
	hourStep?: number;
	tooltip?: object;
	isOptional?: boolean;
	alignCenter?: boolean;
	hideRequired?: boolean;
	firstUpper?: boolean;
	workplaceId?: string;
	portal?: boolean;
	enablePicker?: boolean;
	min?: number;
	max?: number;
	allowZero?: boolean;
	disabled?: boolean;
	module?: QuestionnaireModule;
	patientId?: string;
	excludedIds?: string[];
	workplaceType?: string;
	workplaceTypes?: string[];
	excludeRoleKeys?: WorkplaceAclRole[];
	useTermItemValue?: boolean;
}

const MagicFormItem: React.FC<MagicFormItemProps> = props => {
	const { itemScheme, name } = props;
	const disabled = props?.disabled || itemScheme?.settings?.disabled;
	const showCharCounter =
		itemScheme?.settings?.showCharCounter ?? props?.showCharCounter;
	const { t } = useTranslation();
	const { currency } = useAppContext();
	const { maxLength } = useMagicItemRules(itemScheme);

	const {
		type,
		options,
		children,
		onChange,
		inLine = false,
		settings,
	} = props.itemScheme;

	const label = itemScheme?.label || itemScheme?.title;

	const finalName = name || itemScheme?.key;

	const handleChange = () => {
		!!onChange && onChange();
	};

	const loopChildren = () => {
		return itemScheme?.children?.map((subItem, subIndex) => {
			return (
				<MagicFormItem
					key={subIndex}
					itemScheme={subItem}
					disabled={disabled}
					showCharCounter={showCharCounter}
				/>
			);
		});
	};

	let component = <></>;

	switch (type) {
		case MagicField.COLLAPSE:
			component = <Collapse title={label}>{loopChildren()}</Collapse>;
			break;
		case MagicField.CONDITION:
			component = (
				<FormCondition
					controlName={itemScheme.value}
					equal={itemScheme?.settings?.equal}
					in={itemScheme?.settings?.in}
					rules={itemScheme?.settings?.rules}>
					{loopChildren()}
				</FormCondition>
			);
			break;
		case MagicField.MULTIPLIER:
			component = (
				<FormMultiplier
					name={finalName}
					createDefault={itemScheme?.settings?.createDefault}
					max={itemScheme?.settings?.max}
					min={itemScheme?.settings?.min}
					addButtonTitle={itemScheme?.settings?.addButtonTitle}>
					{loopChildren()}
				</FormMultiplier>
			);
			break;
		case MagicField.MULTIPLIER_REMOVE_BUTTON:
			component = (
				<div className={'d-flex justify-content-end'}>
					<FormMultiplierRemoveButton>
						<RemoveButton onClick={null} title={'base.shared.remove'} />
					</FormMultiplierRemoveButton>
				</div>
			);
			break;
		case MagicField.CONTAINER:
			component = (
				<FormContainer name={finalName} settings={itemScheme?.settings}>
					{loopChildren()}
				</FormContainer>
			);
			break;
		case MagicField.CHECKBOX:
			component = (
				<FormControl
					name={finalName}
					hideRequired={itemScheme?.settings?.hideRequired ?? true}>
					<Checkbox
						tx={label}
						disabled={disabled}
						firstUpper={itemScheme?.settings?.firstUpper ?? false}
					/>
				</FormControl>
			);
			break;
		case MagicField.TEXT:
			component = <MagicFieldText itemScheme={itemScheme} />;
			break;
		case MagicField.IMAGE:
			component = <MagicFieldImage itemScheme={itemScheme} />;
			break;
		case MagicField.EMAIL:
		case MagicField.TEXT_FIELD:
			component = (
				<InputWrapper>
					<MagicFieldTextField
						name={finalName}
						itemScheme={itemScheme}
						disabled={disabled}
						showCharCounter={showCharCounter}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.TEXTAREA:
			component = (
				<InputWrapper>
					<TextAreaField
						label={label}
						name={finalName}
						isRequired={settings?.required}
						hideRequired={settings?.hideRequired}
						labelStyle={settings?.labelStyle}
						onChange={onChange}
						disabled={disabled}
						charactersLimit={maxLength}
						showCharCounter={showCharCounter}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.SELECT:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<Select
						disabled={disabled}
						returnKey={settings?.returnKey}
						options={itemScheme?.children?.map(item => {
							return {
								...item,
								label: t(item?.title),
							};
						})}
					/>
				</FormControl>
			);
			break;
		case MagicField.QUESTIONNAIRE_SELECT:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<QuestionnaireSelect
						enablePicker={settings?.enablePicker}
						module={settings?.module}
					/>
				</FormControl>
			);
			break;
		case MagicField.PATIENT_SELECT:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<PatientSelect />
				</FormControl>
			);
			break;
		case MagicField.DOCTOR_SELECT:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<DoctorSelect />
				</FormControl>
			);
			break;
		case MagicField.DATEPICKER:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<DatePicker
						placeholder={settings?.placeholder}
						disabled={disabled}
						rules={settings?.rules}
					/>
				</FormControl>
			);
			break;
		case MagicField.RADIOLIST:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}
					labelStyle={settings?.labelStyle}>
					<RadioList
						data={itemScheme?.children}
						inLine={inLine}
						disabled={disabled}
					/>
				</FormControl>
			);
			break;
		case MagicField.HR:
			component = <hr />;
			break;
		case MagicField.BLOODPRESSURE:
			component = (
				<>
					<Label tx={label} />
					<div
						className='d-flex flex-row align-items-center'
						style={{ gap: '10px' }}>
						<MagicFormItem
							disabled={disabled}
							itemScheme={{
								type: MagicField.TEXT_FIELD,
								label: 'Sistólica',
								key: 'bloodPressure.systolic',
								settings: {
									suffix: 'mmHg',
									required: settings?.required,
								},
								inLine: true,
							}}
						/>
						<MagicFormItem
							disabled={disabled}
							itemScheme={{
								type: MagicField.TEXT_FIELD,
								label: 'Diastólica',
								key: 'bloodPressure.diastolic',
								settings: {
									suffix: 'mmHg',
									required: settings?.required,
								},
								inLine: true,
							}}
						/>
					</div>
				</>
			);
			break;
		case MagicField.GLUCOSE:
		case MagicField.GLYCAEMIA:
			component = (
				<GlycaemiaFormControl
					name={finalName}
					label={label}
					disabled={disabled}
				/>
			);
			break;
		case MagicField.BMI:
			component = (
				<BMIFormControl
					name={finalName}
					label={label}
					weightKey={itemScheme?.settings?.weightKey}
					heightKey={itemScheme?.settings?.heightKey}
				/>
			);
			break;
		case MagicField.DIAGNOSIS_LATAM:
			component = (
				<FormControl
					label={label}
					name={finalName}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}>
					<DiagnosisLatam />
				</FormControl>
			);
			break;
		case MagicField.HSTACK:
			component = (
				<HStack space={3} alignCenter={itemScheme?.settings?.alignCenter}>
					{loopChildren()}
				</HStack>
			);
			break;
		case MagicField.WHITESPACE:
			component = <div style={{ height: '20px' }} />;
			break;
		case MagicField.TIMEPICKER:
			component = (
				<FormControl
					name={finalName}
					label={label}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}>
					<TimePicker
						placeholder={settings?.placeholder}
						minuteStep={settings?.minuteStep}
						hourStep={settings?.hourStep}
					/>
				</FormControl>
			);
			break;
		case MagicField.ORGANIZATION_UNIT_SELECT:
			component = (
				<FormControl
					name={finalName}
					label={label}
					isRequired={settings?.required}
					labelStyle={settings?.labelStyle}>
					<WorkplaceOrganizationUnitSelect
						workplaceId={settings?.workplaceId}
						portal={settings?.portal}
						excludedIds={settings?.excludedIds}
					/>
				</FormControl>
			);
			break;
		case MagicField.PRICE_WITH_CURRENCY:
			component = (
				<InputWrapper>
					<FormattedPriceInputField
						name={finalName ?? ''}
						disabled={disabled}
						label={itemScheme?.title}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.PHONE_NUMBER:
			component = (
				<InputWrapper>
					<PhoneNumberField
						name={finalName ?? ''}
						disabled={disabled}
						label={itemScheme?.title}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.NZCI_VACCINATION_KIND:
			component = (
				<InputWrapper>
					<NCZIReferenceSelectField
						label={label}
						name={finalName ?? ''}
						oid={NCZI_OID.VACCINATION_KIND}
						isRequired
					/>
				</InputWrapper>
			);
			break;
		case MagicField.NZCI_VACCINATION_TYPE:
			component = (
				<InputWrapper>
					<NCZIReferenceSelectField
						label={label}
						name={finalName ?? ''}
						oid={NCZI_OID.VACCINATION_TYPE}
						isRequired
					/>
				</InputWrapper>
			);
			break;
		case MagicField.NZCI_SPECIALIZATION:
			component = (
				<InputWrapper>
					<NCZIReferenceSearchSelectField
						label={label}
						name={finalName ?? ''}
						oid={NCZI_OID.SPECIALIZATION}
						isRequired
					/>
				</InputWrapper>
			);
			break;
		case MagicField.RICH_TEXT_FIELD:
			component = (
				<RichTextField
					label={label}
					name={finalName ?? ''}
					disabled={disabled}
					isRequired={settings?.required}
				/>
			);
			break;
		case MagicField.DIAGNOSIS_CZECH:
			component = (
				<InputWrapper>
					<DiagnosisSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.VZP_REIMBURSEMENT:
			component = (
				<InputWrapper>
					<HealthCareReimbursementVzpSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.CASE_TYPE_REGULATION:
			component = (
				<InputWrapper>
					<HealthCareRegulationCaseTypeSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY:
			component = (
				<InputWrapper>
					<QuestionnaireScheduleFrequencySelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.FIELD_OF_CARE_SUKL_SELECT:
			component = (
				<InputWrapper>
					<FieldOfCareSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.FORM_OF_CARE_SELECT:
			component = (
				<InputWrapper>
					<FormOfCareSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.WORKPLACE_SELECT:
			component = (
				<InputWrapper>
					<WorkplaceSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
						workplaceType={settings?.workplaceType}
						workplaceTypes={settings?.workplaceTypes}
						portal={settings?.portal}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.CITIZENSHIP_SELECT:
			component = (
				<InputWrapper>
					<CitizenshipSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.HEALTH_INSURANCE_COMPANY_SELECT:
			component = (
				<InputWrapper>
					<HealthInsuranceCompanySelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.GENDER:
			component = (
				<InputWrapper>
					<GenderSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.PATIENT_CONSENT_REQUEST:
			component = <PatientNoConsentProvided patientId={settings?.patientId} />;
			break;
		case MagicField.SUKL_MEDICAMENT_SELECT:
			component = (
				<FormControl
					name={finalName}
					label={label}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}>
					<PrescriptionMedicamentsSelect />
				</FormControl>
			);
			break;
		case MagicField.TRANSLATIONS:
			component = (
				<MagicFormTranslations
					name={finalName ?? ''}
					inputType={
						(itemScheme.subType as MagicField) ?? MagicField.TEXT_FIELD
					}
				/>
			);
			break;
		case MagicField.VAT_RATE:
			component = (
				<InputWrapper>
					<VATRateSelectField
						label={label}
						name={finalName ?? ''}
						isRequired={settings?.required}
					/>
				</InputWrapper>
			);
			break;
		case MagicField.NUMBER:
			component = (
				<NumberField
					label={label}
					name={finalName ?? ''}
					isRequired={settings?.required}
				/>
			);
			break;
		case MagicField.ROLE_MULTISELECT_WITH_CHECKLIST:
			component = (
				<RoleMultiSelectWithCheckListField
					label={label}
					name={finalName ?? ''}
					isRequired={settings?.required}
					excludeRoleKeys={settings?.excludeRoleKeys}
					portal
				/>
			);
			break;
		case MagicField.P1_MEDICAMENT:
			component = (
				<FormControl
					name={finalName}
					label={label}
					isRequired={settings?.required}
					hideRequired={settings?.hideRequired}>
					<P1MedicamentFiled name={finalName ?? ''} />
				</FormControl>
			);
			break;
		case MagicField.P1_PAYMENT_COVERAGE_TYPE:
			component = (
				<P1PaymentCoverageTypeField
					name={finalName ?? ''}
					label={label}
					isRequired={settings?.required}
				/>
			);
			break;
		case MagicField.P1_DOSAGE_INTERVAL_TYPE:
			component = (
				<P1DosageIntervalTypeField
					name={finalName ?? ''}
					label={label}
					isRequired={settings?.required}
				/>
			);
			break;
	}

	if (settings?.tooltip?.content) {
		component = (
			<div className='d-flex'>
				<div className='mr-2'>{component}</div>
				<Tooltip
					trigger={
						<img
							alt={'info'}
							width='20px'
							src='/dist/icons/v2/circle-info-solid.svg'
						/>
					}
					position={'top center'}>
					{t(settings.tooltip.content)}
				</Tooltip>
			</div>
		);
	}

	if (itemScheme?.settings?.hidden) {
		component = <div style={{ display: 'none' }}>{component}</div>;
	}

	return component;
};

export default MagicFormItem;
